import { Injectable, computed, inject } from '@angular/core';
import { AuthService } from '@features/auth/services/auth.service';
import {
  AccessProfile,
  PermAction,
  PortalModuleId,
  allowedBusinessIds,
  allowedModules,
  buildAccessProfile,
  canDo,
  grantedPageBeIds,
  hasModule,
  inBranchScope,
  isAllBranches,
  isAllBusinesses,
  isCatalogueTabVisible,
  landingRouteFor,
} from '@core/models/permissions.model';

/**
 * Single source of truth for BE-288 portal authorization. Reactively derives the
 * current user's AccessProfile from the auth profile and exposes the queries the
 * sidebar, guards, action directive, and branch pickers all read from.
 *
 * Note: the FE is a UX layer only — the backend is the security boundary and
 * enforces branch/module scope on every request (BE-288). Hiding a button or a
 * route here is convenience, not protection.
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private authService = inject(AuthService);

  /** Recomputes whenever the logged-in profile changes. */
  readonly profile = computed<AccessProfile>(() => buildAccessProfile(this.authService.currentUser()));

  readonly unrestricted = computed(() => this.profile().unrestricted);
  readonly isCallScreenOnly = computed(() => this.profile().callScreenOnly);
  readonly modules = computed<PortalModuleId[]>(() => allowedModules(this.profile()));

  /**
   * WAKI-admin (unrestricted) tier. Some actions — creating a business user,
   * branch, inventory, or a manual transaction — are platform-setup operations
   * reserved for admins, never granted to a branch-scoped business user even if
   * they hold that module's create flag.
   */
  readonly isAdmin = computed(() => this.profile().unrestricted);

  /** Can the user perform `action` on `moduleId`? */
  can(moduleId: PortalModuleId, action: PermAction): boolean {
    return canDo(this.profile(), moduleId, action);
  }

  /** Is the module visible/reachable (has at least view)? */
  hasModule(moduleId: PortalModuleId): boolean {
    return hasModule(this.profile(), moduleId);
  }

  /**
   * Page beIds the user may see under `moduleId`, or `null` when the module
   * isn't page-restricted (show every page). Drives sidebar page-level filtering
   * — the caller maps beIds to sidebar routes via the page catalog.
   */
  grantedPages(moduleId: PortalModuleId): Set<string> | null {
    return grantedPageBeIds(this.profile(), moduleId);
  }

  /** FE-295: is this Inventory > Catalogue tab (product type) granted? */
  isCatalogueTabVisible(type: string): boolean {
    return isCatalogueTabVisible(this.profile(), type);
  }

  // ── Branch scope ──
  isAllBranches(): boolean {
    return isAllBranches(this.profile());
  }

  allowedBranchIds(): string[] {
    return this.profile().branchIds;
  }

  inBranchScope(branchId: string): boolean {
    return inBranchScope(this.profile(), branchId);
  }

  // ── Business scope ──
  isAllBusinesses(): boolean {
    return isAllBusinesses(this.profile());
  }

  allowedBusinessIds(): string[] {
    return allowedBusinessIds(this.profile());
  }

  // ── Routing ──
  landingRoute(): string {
    return landingRouteFor(this.profile());
  }

  /**
   * Parses a spec used by the wakiCan directive:
   *  - `"admin"`          → admin-only (unrestricted) actions
   *  - `"module"`         → module visible (has view)
   *  - `"module:create"`  → specific action on the module
   */
  check(spec: string): boolean {
    if (spec === 'admin') return this.isAdmin();
    const [moduleId, action] = spec.split(':') as [PortalModuleId, PermAction?];
    return action ? this.can(moduleId, action) : this.hasModule(moduleId);
  }
}
